<template>
    <div class="container">
        <div v-if="!isFormVisible" class="card">
            <div class="card-header d-flex justify-content-between align-items-center ps-4 pt-3 pb-4">
                <h5><i class="fa-duotone fa-solid fa-list"></i> Lista de Ventas</h5>
                <argon-button size="sm" color="primary" variant="gradient" @click="openCreateModalWithDate">
                    <span class="fa-solid fa-plus" />
                    Crear
                </argon-button>
            </div>
            <div class="card-body pt-0">
                <CustomFilters
:order-options="orderOptions" :items-per-page="itemsPerPage" :view-as-card="viewAsCard"
                    :show-deleted="showDeleted" @apply-filter="applyFilter" @update-items-per-page="updateItemsPerPage"
                    @update-view-as-card="updateViewAsCard" @update-show-deleted="updateShowDeleted" />
                <div v-if="loading" class="loading-indicator">
                    <LoadingData :text="'Cargando ventas...'" />
                </div>
                <div v-else>
                    <div v-if="filteredItems.length === 0" class="no-records p-5 px-4">
                        <p>No se encontraron registros. Intenta ajustar los filtros o añadir nuevos registros.</p>
                    </div>
                    <div v-else>
                        <div v-if="!viewAsCard" class="table-responsive">
                            <GenericTable
:data="filteredItems" :headers="headers" @delete-record="deleteItem"
                                @edit-record="openEditModal" @restore-record="restoreItem" />
                        </div>
                        <transition-group
ref="listContainer" name="fade-move" tag="div"
                            class="sale-container me-2 ms-2 pt-2">
                            <template v-if="viewAsCard">
                                <GenericCard
v-for="(item) in filteredItems" :key="item.id" ref="listItems" :data="item"
                                    class="fade-card" @delete-record="deleteItem" @edit-record="openEditModal"
                                    @restore-record="restoreItem" />
                            </template>
                        </transition-group>
                        <Pagination
:total-items="totalFilteredItems" :current-page="currentPage" :last-page="lastPage"
                            :items-per-page="itemsPerPage" @page-change="changePage" />
                    </div>
                </div>
            </div>
        </div>
        <ModelModal
v-if="showModal" v-model="showModal" :is-edit-mode="isEditMode" :resource-api="resourceApi"
            :entity-data="entityData" :display-as-form="displayAsForm" :keep-form-open="keepFormOpen"
            :resource-name="resourceName" @entity-updated="handleEntityUpdated">
            <template #default="{ entity, errors }">
                <div class="mb-3">
                    <label for="name" class="form-label">Nombre</label>
                    <input
id="name" v-model="entity.name" type="text" class="form-control"
                        :class="{ 'is-invalid': errors.name }">
                    <div v-if="errors.name" class="invalid-feedback">
                        {{ errors.name[0] }}
                    </div>
                </div>
            </template>
        </ModelModal>

        <div v-if="isFormVisible && displayAsForm" class="card">
            <div class="card-header d-flex justify-content-between align-items-center ps-4 pt-3 pb-4">
                <h5><i class="fa-duotone fa-solid fa-list"></i> {{ isEditMode ? 'Editar' : 'Crear' }} Punto de Venta
                </h5>
                <argon-button size="sm" color="primary" variant="gradient" @click="closeForm">
                    <span class="fa-solid fa-arrow-left" />
                    Volver
                </argon-button>
            </div>
            <div class="card-body pt-0">
                <ModelModal
v-model="showModal" :is-edit-mode="isEditMode" :resource-api="resourceApi"
                    :entity-data="entityData" :display-as-form="displayAsForm" :keep-form-open="keepFormOpen"
                    :resource-name="resourceName" @entity-updated="handleEntityUpdated">
                    <template #default="{ entity, errors }">
                        <div class="form-grid">
                            <!-- Primera fila: Médico y Fecha de Venta -->
                            <div class="row-grid">

                                <div class="mb-3">
                                    <div :class="{ 'is-invalid': errors.customer_id }">
                                        <MenuSelectedPatient
v-model="entity.customer_id" label="Paciente"
                                            :can-edit-and-add="true" />
                                    </div>
                                    <div v-if="errors.customer_id" class="invalid-feedback">
                                        {{ errors.customer_id[0] }}
                                    </div>
                                </div>

                                <div class="mb-3">
                                    <div :class="{ 'is-invalid': errors.user_id }">
                                        <MenuSelectedFull
v-model="entity.user_id" label="Médico" apiurl="users-doctors"
                                            :can-edit-and-add="false" />
                                    </div>
                                    <div v-if="errors.user_id" class="invalid-feedback">
                                        {{ errors.user_id[0] }}
                                    </div>
                                </div>

                                <div class="mb-3">
                                    <MenuSelectedFull
v-model="entity.status" label="Status" apiurl="sales-statuses"
                                        :can-edit-and-add="false" />
                                </div>

                                <div class="mb-3">
                                    <label for="sale_date" class="form-label">Fecha de Venta</label>
                                    <DatePicker
v-model="entity.sale_date" :errors="errors"
                                        error-field-name="sale_date" />
                                </div>

                            </div>

                            <!-- Segunda fila: Montos (Solo si entity.id existe) -->
                            <div v-show="entity.id" class="amount-grid">

                                <div class="amount-item">
                                    <label for="total_amount">Items</label>
                                    <span id="total_sale_details">{{ entity.total_sale_details }}</span>
                                </div>

                                <div class="amount-item">
                                    <label for="total_amount">Cantidad</label>
                                    <span id="total_quantity">{{ entity.total_quantity }}</span>
                                </div>

                                <div class="amount-item">
                                    <label for="total_amount">Monto Total</label>
                                    <span id="total_amount">$ {{ formatNumber(entity.total_amount) }}</span>
                                </div>

                                <div class="amount-item">
                                    <label for="discount">Descuentos</label>
                                    <span id="discount">$ {{ formatNumber(entity.discount) }}</span>
                                </div>

                                <div class="amount-item">
                                    <label for="subtotal">Subtotal</label>
                                    <span id="dissubtotalcount">$ {{ formatNumber(entity.subtotal) }}</span>
                                </div>

                                <div class="amount-item">
                                    <label for="tax">Impuestos</label>
                                    <span id="tax">$ {{ formatNumber(entity.tax) }}</span>
                                </div>

                                <div class="amount-item">
                                    <label for="final_amount">Monto Final</label>
                                    <span id="final_amount">$ {{ formatNumber(entity.final_amount) }}</span>
                                </div>

                                <div class="amount-item">
                                    <label for="total_payments">Pagos Realizado</label>
                                    <span id="total_payments">$ {{ formatNumber(entity.total_payments) }}</span>
                                </div>

                                <div class="amount-item">
                                    <label for="debts">Adeudos</label>
                                    <span id="debts">$ {{ formatNumber(entity.debts) }}</span>
                                </div>

                                <div v-if="entity.total_payments_refunds" class="amount-item">
                                    <label for="total_payments">Pagos Reembolsados</label>
                                    <span id="total_payments_refunds">$ {{ formatNumber(entity.total_payments_refunds)
                                    }}</span>
                                </div>
                            </div>
                        </div>
                        <div v-show="entity.id" class="tabs">
                            <input id="tab1" v-model="activeTab" type="radio" name="tab-control" value="tab1">
                            <input id="tab2" v-model="activeTab" type="radio" name="tab-control" value="tab2">
                            <input id="tab3" v-model="activeTab" type="radio" name="tab-control" value="tab3">
                            <input id="tab4" v-model="activeTab" type="radio" name="tab-control" value="tab4">
                            <ul>
                                <li><label :class="{ active: activeTab === 'tab1' }" for="tab1">Items</label></li>
                                <li><label :class="{ active: activeTab === 'tab2' }" for="tab2">Carrito</label></li>
                                <li><label :class="{ active: activeTab === 'tab3' }" for="tab3">Pagos</label></li>
                                <li><label :class="{ active: activeTab === 'tab4' }" for="tab4">Impresión</label></li>
                            </ul>
                            <div class="slider"></div>
                            <div class="content">
                                <section v-show="activeTab === 'tab1'">
                                    <Items v-show="entity.id" @get-entity="setDetailsItem" />
                                </section>
                                <section v-show="activeTab === 'tab2'">
                                    <Details
v-if="entity.id" :data="entity" :form-data="setItemtoDetails"
                                        @get-entity="getSale" />
                                </section>
                                <section v-show="activeTab === 'tab3'">
                                    <Payments v-if="entity.id" :data="entity" @get-entity="getSale" />
                                </section>
                                <section v-show="activeTab === 'tab4'">
                                    <Print v-if="entity.id" />
                                </section>
                            </div>
                        </div>
                    </template>
                </ModelModal>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, getCurrentInstance, onMounted } from 'vue';
import { useModelActions } from '@/composables/useModelActions';
import ModelModal from '@/components/ModelModal.vue';
import CustomFilters from './CustomFilters.vue';
import ArgonButton from '@/components/ArgonButton.vue';
import Pagination from '@/components/Pagination.vue';
import GenericCard from './GenericCard.vue';
import GenericTable from './GenericTable.vue';
import DatePicker from "@/components/DatePicker.vue";
import MenuSelectedFull from "@/components/MenuSelectedFull.vue";
import MenuSelectedPatient from '@/components/MenuSelectedPatient.vue';
import Details from './Details.vue';
import Items from './Items.vue';
import Payments from './Payments.vue';
import Print from './Print.vue';
import LoadingData from '@/components/LoadingData.vue';
const { proxy } = getCurrentInstance();

const resourceApi = 'sales';
const resourceName = 'Punto de Venta';

const orderOptions = [
    { text: 'Fecha de modificación', value: 'updated_at' },
    { text: 'Fecha de creación', value: 'created_at' },
    { text: 'Nombre', value: 'name' }
];

const headers = ref([
    { text: 'Monto Total', value: 'total_amount' },
    { text: 'Descuento', value: 'discount' },
    { text: 'Subtotal:', value: 'subtotal' },
    { text: 'IVA:', value: 'tax' },
    { text: 'Total a Pagar:', value: 'final_amount' },
    { text: 'Creación', value: 'created_at' }
]);

const showModal = ref(false);
const isEditMode = ref(false);
const activeTab = ref('tab1');

const entityData = ref({
    name: '',
    description: '',
    subscription: '',
    active: true,
    modules: [],
    user_id: '',
    customer_id: '',
    payment_method_id: '',
    total_amount: '',
    discount: '',
    tax: '',
    final_amount: '',
    status: '',
    comment: '',
    sale_date: ''
});
const viewAsCard = ref(true);
const showDeleted = ref(false);
const loading = ref(true);
const displayAsForm = ref(true);
const keepFormOpen = ref(true);
const isFormVisible = ref(false);

const setItemtoDetails = ref([]);

const {
    openCreateModal,
    openEditModal,
    handleEntityUpdated,
    applyFilter,
    updateItemsPerPage,
    updateViewAsCard,
    updateShowDeleted,
    restoreItem,
    deleteItem,
    fetchItems,
    changePage,
    filteredItems,
    totalFilteredItems,
    currentPage,
    itemsPerPage,
    lastPage
} = useModelActions(resourceApi, {
    showModal,
    isEditMode,
    entityData,
    viewAsCard,
    showDeleted,
    displayAsForm,
    loading,
    isFormVisible,
    keepFormOpen,
    resourceName
});

const setDetailsItem = (formData) => {
    setItemtoDetails.value = formData;
};

const getSale = (updateSale) => {
    proxy.$eventBus.emit('saleUpdated', updateSale);
    entityData.value = {
        ...entityData.value,
        ...updateSale
    };
};

watch([itemsPerPage, currentPage], ([newItemsPerPage, newPage]) => {
    if (newItemsPerPage || newPage) {
        fetchItems(newPage);
    }
});


onMounted(async () => {
    await fetchItems();
});

const closeForm = () => {
    isFormVisible.value = false;
    fetchItems(currentPage.value);
};

const openCreateModalWithDate = () => {
    const currentDate = new Date().toISOString().split('T')[0];
    openCreateModal({ type: 'sale_date', sale_date: currentDate, status: '1', total_amount: 0, discount: 0, tax: 0, final_amount: 0 });
};

const formatNumber = (number) => {
    if (number === undefined || number === null) {
        return '0.00';
    }
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
</script>

<style scoped>
.sale-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
    justify-content: center;
    cursor: pointer;
}

.tabs {
    position: relative;
    margin: 2rem 0;
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.tabs ul {
    display: flex;
    list-style: none;
    padding: 0;
    margin: 0;
    border-bottom: 3px solid #007bff;
    justify-content: space-around;
}

.tabs ul li {
    flex: 1;
    text-align: center;
}

.tabs ul li label {
    display: block;
    margin: 0;
    padding: 1rem;
    cursor: pointer;
    transition: background 0.3s ease, color 0.3s ease;
    font-weight: bold;
    border-radius: 8px 8px 0 0;
}

.tabs ul li label:hover {
    background: rgba(0, 123, 255, 0.1);
    color: #007bff;
}

.tabs ul li label.active {
    background: #007bff;
    color: white;
}

.tabs .slider {
    position: absolute;
    bottom: 0;
    width: 50%;
    height: 5px;
    background: #007bff;
    border-radius: 5px;
    transition: transform 0.3s ease;
}

.tabs .content {
    padding: 2rem;
    background: white;
    border-radius: 12px;
    box-shadow: none;
    animation: fadeIn 0.5s ease-in-out;
}

.tabs .content section {
    display: none;
}

.tabs input:nth-of-type(1):checked~.slider {
    transform: translateX(0%);
}

.tabs input:nth-of-type(2):checked~.slider {
    transform: translateX(100%);
}

.tabs input:nth-of-type(3):checked~.slider {
    transform: translateX(100%);
}

.tabs input:nth-of-type(4):checked~.slider {
    transform: translateX(100%);
}

.tabs input:nth-of-type(1):checked~.content section:nth-of-type(1) {
    display: block;
}

.tabs input:nth-of-type(2):checked~.content section:nth-of-type(2) {
    display: block;
}

.tabs input:nth-of-type(3):checked~.content section:nth-of-type(3) {
    display: block;
}

.tabs input:nth-of-type(4):checked~.content section:nth-of-type(4) {
    display: block;
}

.tabs input {
    position: absolute;
    opacity: 0;
    visibility: hidden;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.amount-grid {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
}

.row-grid {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
}

.row-grid .mb-3 {
    flex: 1;
}

.row-grid .mb-3:first-child {
    margin-right: 1rem;
}

.amount-grid .amount-item {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.amount-grid .amount-item label {
    font-weight: bold;
    margin-bottom: 0.5rem;
    font-size: .7rem;
}

.amount-grid .amount-item span {
    font-size: 1.2rem;
    color: #007bff;
    font-weight: bold;
    background: #f0f8ff;
    padding: 0.5rem 0.4rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: background 0.3s ease, color 0.3s ease;
}

.amount-grid .amount-item span:hover {
    background: #007bff;
    color: #fff;
}
</style>
